export interface PoolOptions<R> {
  concurrency?: number;
  min_concurrency?: number;
  success_streak_to_grow?: number;
  is_failure?: (result: R) => boolean;
  on_progress?: (done: number, total: number) => void;
}

export function runAdaptivePool<T, R>(
  items: T[],
  worker: (item: T, index: number) => Promise<R>,
  options: PoolOptions<R> = {}
): Promise<R[]> {
  const start_concurrency = options.concurrency || 5;
  const min_concurrency = options.min_concurrency || 1;
  const success_streak_to_grow = options.success_streak_to_grow || 5;
  const is_failure = options.is_failure || (() => false);

  const results: R[] = new Array(items.length);
  let limit = start_concurrency;
  let next_index = 0;
  let active = 0;
  let done = 0;
  let success_streak = 0;

  return new Promise((resolve) => {
    if (!items.length) {
      resolve(results);
      return;
    }

    const launch = () => {
      if (next_index >= items.length && active === 0) {
        resolve(results);
        return;
      }

      while (active < limit && next_index < items.length) {
        const index = next_index;
        next_index++;
        active++;

        Promise.resolve()
          .then(() => worker(items[index], index))
          .then((result) => {
            results[index] = result;
            return is_failure(result);
          })
          .catch((e) => {
            results[index] = e;
            return true;
          })
          .then((failed) => {
            if (failed) {
              success_streak = 0;
              limit = Math.max(min_concurrency, Math.floor(limit / 2));
            } else {
              success_streak++;
              if (success_streak >= success_streak_to_grow && limit < start_concurrency) {
                limit++;
                success_streak = 0;
              }
            }
            active--;
            done++;
            if (options.on_progress) options.on_progress(done, items.length);
            launch();
          });
      }
    };

    launch();
  });
}
